import { useContext, useMemo, useRef, useState } from 'react'
import styles from "../css/AddNote.module.css"
import { colors } from '../../../data/colorsDict'
import TagMenu from '../../board/js/TagMenu';
import { IoClose } from 'react-icons/io5';
import LoadingButton from '../../Loaders/js/LoadingButton';
import { useDispatch, useSelector } from 'react-redux';
import { addNote } from '../../../actions/noteActions';
import { FaFeather } from 'react-icons/fa';
import NoteEditor from './NoteEditor';
import useOverflow from '../../../hooks/useOverflow';
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import CharacterCount from '@tiptap/extension-character-count'
import CodeBlock from '@tiptap/extension-code-block'
import Placeholder from '@tiptap/extension-placeholder'
import { PageContext } from '../../board/js/Notes';
import { TAG_RESET } from '../../../actions/tagTypes';
import UseMobileLayout from '../../../hooks/useMobileLayout';

const AddNote = () => {
    const [note,setNote] = useState({title:"",content:"<p></p>",color:null,tags:[]})
    const [errors,setErrors] = useState({})
    const [focused,setFocused] = useState(false)
    const [showTags,setShowTags] = useState(false)
    const formRef = useRef(null)
    const textRef = useRef(null)
    const hasOverflow = useOverflow(formRef)
    const loading = useSelector((state) => state.noteState.loading)
    const dispatch = useDispatch()
    const [,setPageModals] = useContext(PageContext)
    UseMobileLayout()

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: false,
                heading: { levels: [1, 2, 3] },
                horizontalRule: true,
            }),
            CodeBlock.configure({
                HTMLAttributes: {
                    class: 'ProsemirrorEditor-codeBlock',
                },
            }),
            Underline,
            Placeholder.configure({
                placeholder: 'Start writing...',
            }),
            CharacterCount
        ],
        content: note.content,
        onUpdate: ({ editor }) => {
            if (editor.storage.characterCount.characters() > 25 && errors.content === "At least 25 characters"){
                setErrors((prevErrors)=>{
                    let copy = {...prevErrors}
                    delete copy.content
                    return copy
                })
            }
            setNote({...note,content:editor.getHTML()})
        },
        editorProps: {
            transformPastedHTML(html) {
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')
                const allowedTags = new Set([
                    'B', 'STRONG', 'I', 'EM', 'U', 'S',
                    'H1', 'H2', 'H3',
                    'UL', 'OL', 'LI',
                    'CODE', 'PRE', 'HR',
                    'P', 'BR', 'A'
                ])
                function clean(node) {
                    for (const child of [...node.childNodes]) {
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            if (!allowedTags.has(child.tagName)) {
                                child.replaceWith(...child.childNodes)
                            } else {
                                [...child.attributes].forEach(attr => child.removeAttribute(attr.name))
                                clean(child)
                            }
                        }
                    }
                }

                clean(doc.body)
                return doc.body.innerHTML
            },
            handleKeyDown: (view, event) => {
                if (event.key === 'Enter' && editor.isEmpty) {
                    event.preventDefault()
                    editor.chain().focus().setHardBreak().run()
                    return true
                }
            }
        }
    })

    const handleChange = (e) => {
        let name = e.target.name
        let value = e.target.value
        value = value.replace(/\s\s+/g, ' ');
        setNote({...note,[name]:value})
        if (errors[name]){
            setErrors((prevErrors)=>{
              let copy = {...prevErrors}
              delete copy[name]
              return copy
            })
        }
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        document.activeElement.blur()
        let valid = true
        if (note.title.trim().length < 2){
            valid = false
            setErrors((prevErrors)=> {return {...prevErrors,title:"At least 2 characters"}})
        }
        if (editor.storage.characterCount.characters() < 25){
            valid = false
            setErrors((prevErrors)=> {return {...prevErrors,content:"At least 25 characters"}})
        }
        if (valid){
            try{
                await dispatch(addNote(note))
                closeModal()
            }
            catch(err){
                setErrors({...errors,...err.response.data})
            }
        }
    }

    const removeTag = (index) => {
        let tags = [...note.tags]
        tags.splice(index,1)
        setNote({...note,tags:tags})
    }

    const isDisabled = useMemo(()=>{
        if (!note.title || !note.color || !note.content){
            return true
        }
        if (Object.keys(errors).length){
            return true
        }
    },[note,errors])

    const handleKeyDown = (e) => {
        const isPrintableCharacter = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey
        if (isPrintableCharacter){
            if (note.title.length === 30){
                if (e.target.parentNode){
                    e.target.parentNode?.classList.add(styles.shake)
                }
                setTimeout(()=>{
                if (e.target.parentNode){
                    e.target.parentNode?.classList.remove(styles.shake)
                }
                },300)
            }
        }
    }

    const deleteTag = (tag_id) => {
        setNote((prevState)=>{
            let tags = [...prevState.tags]
            tags = tags.filter(tag => tag._id !== tag_id)
            return {...prevState,tags:tags}
        })
    }

    const handleScroll = () => {
        if (showTags){
            closeTagMenu()
        }
    }

    const closeModal = () => {
        setPageModals((prevState)=>{
            let copy = {...prevState}
            copy.add = false
            return copy
        })
    }

    const openTagMenu = () => {
        dispatch({type: TAG_RESET})
        setShowTags(true)
    }

    const closeTagMenu = () => {
        setShowTags(false)
        dispatch({type: TAG_RESET})
    }

    const handleTagMenu = () => {
        if (showTags){
            closeTagMenu()
        }
        else{
            openTagMenu()
        }
    }

    return (
        <div data-testid = "add-modal" className = {`${styles.wrapper} modal-wrapper`} onScroll = {()=>handleScroll()}>
            <div className = {`${styles.innerWrapper} inner-wrapper`}>
                <form className = {`${styles.box} nested-container ${hasOverflow ? styles.overflowed : ""}`} onSubmit = {handleSubmit}>
                    <header>
                        <div>
                            <div className = {styles.icon}>
                                <div className = {styles.feathers}>
                                    <FaFeather className={styles.featherShadow}/>
                                    <FaFeather className={styles.featherMain}/>
                                </div>
                            </div>
                            <div className = {styles.title}>Create a new note</div>
                            <div className = {styles.sub}>Add another note to your collection</div>
                        </div>
                        <button data-testid = "close-btn" type = "button" className = {styles.closeBtn} onClick = {()=>closeModal()}><IoClose/></button>
                    </header>
                    <div className = {`${styles.content} nested-container `} ref = {formRef} onScroll = {()=>handleScroll()}>
                        <div className = {styles.contentWrapper}>
                            <div className = {styles.inputArea}>
                                <div className = {styles.labelWrapper}>
                                    <label htmlFor='title'>Note Title</label>
                                    {focused && <div className = {styles.characterLength}>{note.title.length}/30</div>}
                                </div>
                                <div className = {`${styles.inputBox} ${errors.title ? styles.errorBox : ""}`} onKeyDown = {handleKeyDown}>
                                    <input
                                        type = "text"
                                        name = "title"
                                        value = {note.title}
                                        maxLength = {30}
                                        onChange = {handleChange}
                                        placeholder = "Title"
                                        onFocus = {()=>setFocused(true)}
                                        onBlur = {()=>setFocused(false)}
                                    />
                                </div>
                                {errors.title && <div data-testid = "title-error" className = {styles.error}>{errors.title}</div>}
                            </div>
                            <div className = {`${styles.inputArea} ${styles.colorArea}`}>
                                <label htmlFor = "color">Color</label>
                                <div className = {styles.colors}>
                                    {colors.map((color,index)=>(
                                        <div 
                                            data-testid = {`color-${color.name}`}
                                            className = {`${styles.colorBox} ${color.name === note.color ? styles.active : ""}`}
                                            key = {index}
                                            style = {{ backgroundColor: color.code, boxShadow: color.name === note.color && `0 0 0 3px ${color.code}` }}
                                            onClick = {()=>setNote({...note,color:color.name})}
                                        >
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className = {styles.inputArea}>
                                <label htmlFor = "tags">Tags</label>
                                <div className = {styles.tags}>
                                    {note.tags.map((tag,index)=>(
                                        <div className = {styles.tag} key = {index}>
                                            <div className = {styles.tagName}>{tag.name}</div>
                                            <button type = "button" className = {styles.removeTag} onClick = {()=>removeTag(index)}><IoClose/></button>
                                        </div>
                                    ))}
                                    {note.tags.length < 5 && <button data-testid = "tag-btn" type = "button" className = {`${styles.tagBtn} ${showTags ? styles.active : ""}`} onClick = {()=>handleTagMenu()}>+ Add Tag</button>}
                                    {showTags &&
                                        <TagMenu
                                            selectedTags = {note.tags}
                                            addTag = {(tag) => {
                                                setNote(prev => {
                                                    const updatedTags = [...prev.tags,tag]
                                                    if (updatedTags.length === 5) {
                                                        setShowTags(false)
                                                    }
                                                    return {...prev,tags: updatedTags}
                                                })
                                            }}
                                            removeTag = {deleteTag}
                                            closeMenu = {()=>closeTagMenu()}
                                            modal = "add-note"
                                        />}
                                </div>
                            </div>
                            <div className = {styles.inputArea}>
                                <div className = {styles.labelWrapper}>
                                    <label htmlFor='content'>Content</label>
                                </div>
                                <div className = {`${styles.textBox} ${errors.content ? styles.errorBox : ""}`} ref = {textRef}>
                                    <NoteEditor isEmpty = {editor.isEmpty} editor = {editor}/>
                                </div>
                                {errors.content && <div data-testid = "content-error" className = {styles.error}>{errors.content}</div>}
                            </div>
                        </div>
                    </div>
                    <footer className = {`${!hasOverflow ? "add-footer": ""}`}>
                        <button type = "submit" className = {`${styles.submitBtn} ${loading ? styles.loading : ""}`} disabled = {isDisabled}>
                            Create Note →
                            {loading && <LoadingButton/>}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
  )
}

export default AddNote